/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  Pagination,
  Chip,
  Paper,
  InputAdornment,
  CircularProgress,
  Collapse,
  Avatar,
  Tooltip,
  Fade,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
  Badge,
  Divider,
  LinearProgress,
  Switch,
  FormControlLabel
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SaveIcon from '@mui/icons-material/Save';
import SharedPlanChip from '../components/PlanChip';
import RefreshIcon from '@mui/icons-material/Refresh';
import EmailIcon from '@mui/icons-material/Email';
import StorefrontIcon from '@mui/icons-material/Storefront';
import VerifiedIcon from '@mui/icons-material/Verified';
import ApiService from '../services/api';

// ============ Styled Components ============

const PageContainer = styled(Box)(({ theme }) => ({
  padding: '32px',
  backgroundColor: '#F8FAFC',
  minHeight: '100vh',
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  marginBottom: '32px',
}));

const PageTitle = styled(Typography)(({ theme }) => ({
  fontSize: '28px',
  fontWeight: 700,
  color: '#1E293B',
  marginBottom: '8px',
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
}));

const PageSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  color: '#64748B',
}));

const StatsRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '16px',
  marginBottom: '24px',
  flexWrap: 'wrap',
}));

const StatCard = styled(Paper)(({ theme }) => ({
  padding: '20px 24px',
  borderRadius: '12px',
  border: '1px solid #E2E8F0',
  boxShadow: 'none',
  flex: '1',
  minWidth: '180px',
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
  transition: 'all 0.2s ease',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    transform: 'translateY(-2px)',
  },
}));

const StatIcon = styled(Box)(({ color }) => ({
  width: '48px',
  height: '48px',
  borderRadius: '12px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: alpha(color || '#3B82F6', 0.1),
  color: color || '#3B82F6',
}));

const StatValue = styled(Typography)(({ theme }) => ({
  fontSize: '24px',
  fontWeight: 700,
  color: '#1E293B',
  lineHeight: 1.2,
}));

const StatLabel = styled(Typography)(({ theme }) => ({
  fontSize: '13px',
  color: '#64748B',
}));

const ToolbarSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '24px',
  gap: '16px',
  flexWrap: 'wrap',
}));

const SearchField = styled(TextField)(({ theme }) => ({
  flex: 1,
  maxWidth: '400px',
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#fff',
    borderRadius: '10px',
    '& fieldset': {
      borderColor: '#E2E8F0',
    },
    '&:hover fieldset': {
      borderColor: '#CBD5E1',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#3B82F6',
      borderWidth: '1px',
    },
  },
}));

const AddButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#3B82F6',
  color: '#fff',
  borderRadius: '10px',
  padding: '10px 20px',
  fontWeight: 600,
  textTransform: 'none',
  boxShadow: '0 2px 8px rgba(59, 130, 246, 0.25)',
  '&:hover': {
    backgroundColor: '#2563EB',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.35)',
  },
}));

const RefreshButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: '#fff',
  border: '1px solid #E2E8F0',
  borderRadius: '10px',
  padding: '10px',
  '&:hover': {
    backgroundColor: '#F8FAFC',
    borderColor: '#CBD5E1',
  },
}));

const EmailCard = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'isExpanded'
})(({ isExpanded }) => ({
  borderRadius: '12px',
  border: '1px solid #E2E8F0',
  boxShadow: 'none',
  marginBottom: '12px',
  overflow: 'hidden',
  transition: 'all 0.2s ease',
  backgroundColor: isExpanded ? '#FAFBFC' : '#fff',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
    borderColor: '#CBD5E1',
  },
}));

const EmailCardHeader = styled(Box)(({ theme }) => ({
  padding: '16px 20px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: '#F8FAFC',
  },
}));

const EmailAvatar = styled(Avatar)(({ theme }) => ({
  width: '44px',
  height: '44px',
  backgroundColor: '#EFF6FF',
  color: '#3B82F6',
  fontWeight: 600,
  fontSize: '16px',
}));

const GmbCard = styled(Box)(({ theme }) => ({
  backgroundColor: '#fff',
  borderRadius: '10px',
  border: '1px solid #E2E8F0',
  padding: '16px',
  marginBottom: '12px',
  transition: 'all 0.2s ease',
  '&:hover': {
    borderColor: '#CBD5E1',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
  },
}));

const PlanSelectorChip = styled(Chip)(({ selected, plancolor }) => ({
  fontWeight: 600,
  fontSize: '12px',
  height: '32px',
  borderRadius: '8px',
  transition: 'all 0.2s ease',
  cursor: 'pointer',
  backgroundColor: selected ? plancolor : '#F1F5F9',
  color: selected ? '#fff' : '#64748B',
  border: selected ? 'none' : '1px solid #E2E8F0',
  '&:hover': {
    backgroundColor: selected ? plancolor : '#E2E8F0',
    transform: 'scale(1.02)',
  },
}));

const SavePlanButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#10B981',
  color: '#fff',
  borderRadius: '8px',
  padding: '6px 16px',
  fontWeight: 600,
  fontSize: '12px',
  textTransform: 'none',
  minWidth: '90px',
  '&:hover': {
    backgroundColor: '#059669',
  },
  '&:disabled': {
    backgroundColor: '#E2E8F0',
    color: '#94A3B8',
  },
}));

const SubscriptionBadge = styled(Box)(({ status }) => {
  const colors = {
    active: { bg: '#DCFCE7', text: '#166534', border: '#86EFAC' },
    expired: { bg: '#FEE2E2', text: '#991B1B', border: '#FECACA' },
    pending: { bg: '#FEF3C7', text: '#92400E', border: '#FDE68A' },
    none: { bg: '#F1F5F9', text: '#64748B', border: '#E2E8F0' },
  };
  const color = colors[status] || colors.none;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '4px 10px',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: 600,
    backgroundColor: color.bg,
    color: color.text,
    border: `1px solid ${color.border}`,
  };
});

const EmptyState = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: '60px 20px',
  color: '#64748B',
}));

// ============ Component ============

export default function AdminGmbManagement() {
  const [allEmails, setAllEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [profilesByEmail, setProfilesByEmail] = useState({});
  const [loadingProfiles, setLoadingProfiles] = useState({});
  const [planSelectionByGmb, setPlanSelectionByGmb] = useState({});
  const [subscriptionsByGmb, setSubscriptionsByGmb] = useState({});
  const [savingPlan, setSavingPlan] = useState({});
  const [aiPostLimitByGmb, setAiPostLimitByGmb] = useState({});
  const [savingAiPostLimit, setSavingAiPostLimit] = useState({});
  const [monthlyPlanByGmb, setMonthlyPlanByGmb] = useState({});
  const [savingMonthlyPlan, setSavingMonthlyPlan] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [addEmailDialogOpen, setAddEmailDialogOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [addingEmail, setAddingEmail] = useState(false);

  const [totalGmbProfiles, setTotalGmbProfiles] = useState(0);
  const itemsPerPage = 10;

  const [serverPagination, setServerPagination] = useState({ page: 1, totalPages: 1, totalItems: 0 });
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Load emails when page or search changes
  useEffect(() => {
    loadEmails(currentPage, debouncedSearch);
  }, [currentPage, debouncedSearch]);

  const loadEmails = async (page = 1, search = '') => {
    setLoading(true);
    try {
      // Use server-side pagination and search
      const queryParams = new URLSearchParams({
        page: String(page),
        limit: String(itemsPerPage),
        ...(search && { search })
      });

      const resp = await ApiService.get(`/admin/gmb-account/all-emails?${queryParams}`);
      const emailList = resp?.data?.emails || resp?.emails || [];
      const stats = resp?.data?.stats || resp?.stats || {};
      const pagination = resp?.data?.pagination || resp?.pagination || {};

      // CRITICAL FIX: Clear cached profile data to ensure fresh data is displayed
      // This prevents stale plan values from being shown after admin updates
      setProfilesByEmail({});

      // Pre-populate plan state and AI post limit from GMB profiles
      // Use fresh maps instead of spreading old state to avoid stale data
      const planMap = {};
      const aiLimitMap = {};
      emailList.forEach(item => {
        (item.gmbs || []).forEach(gmb => {
          if (gmb.id) {
            planMap[String(gmb.id)] = gmb.plan;
            // Use ?? instead of || to preserve 0 values (0 = no AI posts)
            aiLimitMap[String(gmb.id)] = gmb.ai_post_limit ?? null;
            // Monthly plan access from backend
            setMonthlyPlanByGmb(prev => ({ ...prev, [String(gmb.id)]: gmb.monthly_plan_enabled || false }));
          }
        });
      });

      setAllEmails(emailList);
      setPlanSelectionByGmb(planMap);
      setAiPostLimitByGmb(aiLimitMap);
      setTotalGmbProfiles(stats.totalGmbProfiles || 0);
      setServerPagination({
        page: pagination.page || page,
        totalPages: pagination.totalPages || 1,
        totalItems: pagination.totalItems || emailList.length
      });

      // Fetch subscription/payment status for all GMBs so dashboard shows real backend data
      try {
        const locationIds = [];
        emailList.forEach(item => {
          (item.gmbs || []).forEach(g => {
            const id = g.id && String(g.id);
            if (id && !locationIds.includes(id)) locationIds.push(id);
          });
        });

        if (locationIds.length > 0) {
          try {
            // Fetch subscription status for all locations in a single request
            const resp = await ApiService.post('/payments/gmb/bulk', { locationIds });
            if (resp && resp.success && resp.data) {
              setSubscriptionsByGmb(resp.data);
            } else {
              // Fallback to null map
              const subs = {};
              locationIds.forEach(id => { subs[id] = null; });
              setSubscriptionsByGmb(subs);
            }
          } catch (err) {
            console.warn('Bulk payments fetch failed, falling back', err);
            const subs = {};
            locationIds.forEach(id => { subs[id] = null; });
            setSubscriptionsByGmb(subs);
          }
        }
      } catch (err) {
        console.warn('Failed to bulk-fetch subscriptions for GMBs', err);
      }

      console.log('📊 Stats:', {
        totalEmails: stats.totalEmails,
        page: pagination.page,
        totalPages: pagination.totalPages
      });
    } catch (err) {
      console.error('Failed to load emails', err);
      // Fallback: try to load whitelist emails only so admin still sees emails
      try {
        const wl = await ApiService.get('/access/whitelist');
        const entries = wl?.data || wl?.data || wl || [];
        const emailList = (entries || []).map(e => ({
          email: e.email,
          gmbs: [],
          userName: e.addedByAdmin?.name || e.addedByAdmin?.full_name || '',
          userId: e.addedByAdmin?.id || null,
          addedAt: e.createdAt || e.created_at
        }));
        setAllEmails(emailList);
        showSnackbar('Loaded whitelist emails as fallback', 'warning');
      } catch (e2) {
        console.error('Fallback failed to load whitelist emails', e2);
        showSnackbar('Failed to load emails', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Server-side filtering/pagination - allEmails is already filtered and paginated
  const paginatedEmails = allEmails;

  // Stats using server pagination data
  const stats = useMemo(() => {
    const emailsWithGmbs = allEmails.filter(e => (e.gmbs || []).length > 0).length;
    const withPlans = Object.values(planSelectionByGmb).filter(p => p).length;
    const activeSubscriptions = Object.values(subscriptionsByGmb).filter(s => s?.status === 'active').length;
    return {
      totalEmails: serverPagination.totalItems,
      totalGmbs: totalGmbProfiles,
      emailsWithGmbs,
      withPlans,
      activeSubscriptions
    };
  }, [allEmails, planSelectionByGmb, subscriptionsByGmb, totalGmbProfiles, serverPagination]);

  const toggleExpand = async (email) => {
    const isExpanding = !expanded[email];
    setExpanded(prev => ({ ...prev, [email]: isExpanding }));

    if (isExpanding && !profilesByEmail[email]) {
      setLoadingProfiles(prev => ({ ...prev, [email]: true }));

      // Get GMBs linked to THIS specific email from allEmails data
      const entry = allEmails.find(e => e.email === email);

      console.log(`🔍 [toggleExpand] Expanding email: ${email}`);
      console.log(`📋 [toggleExpand] Found entry:`, entry);
      console.log(`📦 [toggleExpand] GMBs from backend:`, entry?.gmbs);

      const gmbs = (entry?.gmbs || []).map(g => {
        console.log(`   Mapping GMB: id=${g.id}, location_id=${g.location_id}, name=${g.gmbName}`);
        return {
          id: String(g.id),
          location_id: g.location_id, // ✅ Include location_id from backend
          gmbName: g.gmbName || g.name || '',
          address: g.address || '',
          isVerified: g.isVerified,
          plan: g.plan || 'none'
        };
      });

      console.log(`✅ [toggleExpand] Mapped ${gmbs.length} profiles for ${email}`);
      gmbs.forEach((g, idx) => {
        console.log(`   ${idx + 1}. id=${g.id}, location_id=${g.location_id}, name=${g.gmbName}`);
      });

      setProfilesByEmail(prev => ({ ...prev, [email]: gmbs }));

      // Fetch subscription status for each GMB (plan is already loaded)
      gmbs.forEach(g => {
        fetchSubscriptionForGmb(g.id);
      });

      setLoadingProfiles(prev => ({ ...prev, [email]: false }));
    }
  };

  // fetchPlanForGmb removed - plan data now comes directly from /all-emails endpoint
  // which returns per-user per-profile plans from GmbAccount.plan field

  const fetchSubscriptionForGmb = async (gmbAccountId) => {
    if (!gmbAccountId) return;
    try {
      // Try to get payment/subscription status for this GMB using database ID
      const resp = await ApiService.get(`/payments/gmb/${encodeURIComponent(gmbAccountId)}`);
      if (resp?.success && resp?.data) {
        setSubscriptionsByGmb(prev => ({ ...prev, [gmbAccountId]: resp.data }));
      } else {
        setSubscriptionsByGmb(prev => ({ ...prev, [gmbAccountId]: null }));
      }
    } catch (err) {
      // No subscription found
      setSubscriptionsByGmb(prev => ({ ...prev, [gmbAccountId]: null }));
    }
  };

  // (trial emails feature removed)

  /**
   * Assign a plan to a SPECIFIC GMB account (per-user, per-profile)
   * @param {string} gmbAccountId - The database ID of the GmbAccount record (unique per user)
   * @param {string} plan - The plan to assign (basic, advance, pro)
   */
  const assignPlan = async (gmbAccountId, plan) => {
    if (!gmbAccountId) return showSnackbar('Missing GMB account ID', 'error');
    if (!plan) return showSnackbar('Please select a plan first', 'warning');

    setSavingPlan(prev => ({ ...prev, [gmbAccountId]: true }));
    try {
      // Use gmb_account_id for per-user plan assignment
      await ApiService.post('/admin/gmb-account/assign-plan', { gmb_account_id: gmbAccountId, plan });
      setPlanSelectionByGmb(prev => ({ ...prev, [gmbAccountId]: plan }));

      // Also update the cached profilesByEmail to reflect the new plan
      setProfilesByEmail(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(email => {
          updated[email] = updated[email].map(g =>
            String(g.id) === String(gmbAccountId) ? { ...g, plan } : g
          );
        });
        return updated;
      });

      // Update allEmails state to reflect the new plan
      setAllEmails(prev => prev.map(item => ({
        ...item,
        gmbs: (item.gmbs || []).map(g =>
          String(g.id) === String(gmbAccountId) ? { ...g, plan } : g
        )
      })));

      showSnackbar(`Plan updated to ${plan.charAt(0).toUpperCase() + plan.slice(1)}`, 'success');
    } catch (err) {
      console.error('Failed to assign plan', err);
      const errorMsg = err.response?.data?.message || 'Failed to assign plan';
      showSnackbar(errorMsg, 'error');
    } finally {
      setSavingPlan(prev => ({ ...prev, [gmbAccountId]: false }));
    }
  };

  /**
   * Set AI post limit for a SPECIFIC GMB account (per-user)
   * @param {string} gmbAccountId - The database ID of the GmbAccount record
   * @param {number|null} limit - The AI post limit (1-10) or null for no limit
   */
  const saveAiPostLimit = async (gmbAccountId, limit) => {
    if (!gmbAccountId) return showSnackbar('Missing GMB account ID', 'error');

    console.log(`[saveAiPostLimit] Saving limit=${limit} for gmbAccountId=${gmbAccountId}`);
    setSavingAiPostLimit(prev => ({ ...prev, [gmbAccountId]: true }));
    try {
      // Use gmb_account_id for per-user AI post limit
      const resp = await ApiService.post('/admin/gmb-account/set-ai-post-limit', {
        gmb_account_id: gmbAccountId,
        ai_post_limit: limit
      });
      console.log(`[saveAiPostLimit] Response:`, resp);
      
      // Use the actual saved value from response
      const savedValue = resp?.data?.ai_post_limit ?? resp?.ai_post_limit ?? limit;
      const saveSuccess = resp?.data?.save_success ?? resp?.save_success ?? true;
      
      if (!saveSuccess) {
        console.error(`[saveAiPostLimit] Backend reported save failure!`);
        showSnackbar('Failed to save AI limit - database error', 'error');
        return;
      }
      
      setAiPostLimitByGmb(prev => ({ ...prev, [gmbAccountId]: savedValue }));
      
      // Also update cached data to keep UI consistent
      setProfilesByEmail(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(email => {
          updated[email] = updated[email].map(g =>
            String(g.id) === String(gmbAccountId) ? { ...g, ai_post_limit: savedValue } : g
          );
        });
        return updated;
      });
      
      setAllEmails(prev => prev.map(item => ({
        ...item,
        gmbs: (item.gmbs || []).map(g =>
          String(g.id) === String(gmbAccountId) ? { ...g, ai_post_limit: savedValue } : g
        )
      })));
      
      showSnackbar(`AI Post limit set to ${savedValue}`, 'success');
    } catch (err) {
      console.error('Failed to set AI post limit', err);
      showSnackbar('Failed to set AI post limit', 'error');
    } finally {
      setSavingAiPostLimit(prev => ({ ...prev, [gmbAccountId]: false }));
    }
  };

  /**
   * Remove plan from a SPECIFIC GMB account (per-user, per-profile)
   * @param {string} gmbAccountId - The database ID of the GmbAccount record
   */
  const removePlan = async (gmbAccountId) => {
    if (!gmbAccountId) return showSnackbar('Missing GMB account ID', 'error');

    setSavingPlan(prev => ({ ...prev, [gmbAccountId]: true }));
    try {
      await ApiService.post('/admin/gmb-account/remove-plan', { gmb_account_id: gmbAccountId });
      setPlanSelectionByGmb(prev => ({ ...prev, [gmbAccountId]: null }));

      // Also update the cached profilesByEmail to reflect the removed plan
      setProfilesByEmail(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(email => {
          updated[email] = updated[email].map(g =>
            String(g.id) === String(gmbAccountId) ? { ...g, plan: null } : g
          );
        });
        return updated;
      });

      // Update allEmails state to reflect the removed plan
      setAllEmails(prev => prev.map(item => ({
        ...item,
        gmbs: (item.gmbs || []).map(g =>
          String(g.id) === String(gmbAccountId) ? { ...g, plan: null } : g
        )
      })));

      showSnackbar('Plan removed successfully', 'success');
    } catch (err) {
      console.error('Failed to remove plan', err);
      const errorMsg = err.response?.data?.message || 'Failed to remove plan';
      showSnackbar(errorMsg, 'error');
    } finally {
      setSavingPlan(prev => ({ ...prev, [gmbAccountId]: false }));
    }
  };

  /**
   * Toggle Monthly Plan access for a SPECIFIC GMB account (per-user)
   * @param {string} gmbAccountId - The database ID of the GmbAccount record
   * @param {boolean} enabled - Whether to enable or disable monthly plan access
   */
  const toggleMonthlyPlan = async (gmbAccountId, enabled) => {
    if (!gmbAccountId) return showSnackbar('Missing GMB account ID', 'error');

    setSavingMonthlyPlan(prev => ({ ...prev, [gmbAccountId]: true }));
    try {
      // Use gmb_account_id for per-user monthly plan toggle
      await ApiService.post('/admin/gmb-account/toggle-monthly-plan', {
        gmb_account_id: gmbAccountId,
        enabled
      });
      setMonthlyPlanByGmb(prev => ({ ...prev, [gmbAccountId]: enabled }));
      showSnackbar(`Monthly Plan ${enabled ? 'enabled' : 'disabled'}`, 'success');
    } catch (err) {
      console.error('Failed to toggle monthly plan', err);
      showSnackbar('Failed to toggle monthly plan', 'error');
    } finally {
      setSavingMonthlyPlan(prev => ({ ...prev, [gmbAccountId]: false }));
    }
  };

  const handleAddEmail = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!email) return showSnackbar('Please enter an email address', 'warning');

    const emailPattern = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
    if (!emailPattern.test(email)) return showSnackbar('Please enter a valid email address', 'warning');

    if (allEmails.some(e => e.email === email)) {
      setNewEmail('');
      setAddEmailDialogOpen(false);
      return showSnackbar('Email already exists', 'warning');
    }

    setAddingEmail(true);
    try {
      // Add email to the allowed_email whitelist table
      await ApiService.post('/access/whitelist', { email });
      setAllEmails(prev => [{ email, gmbs: [], userName: '', userId: null, addedAt: new Date().toISOString() }, ...prev]);
      setNewEmail('');
      setAddEmailDialogOpen(false);
      showSnackbar('Email added successfully', 'success');
    } catch (err) {
      console.error('Failed to add email', err);
      showSnackbar(err.response?.data?.message || 'Failed to add email', 'error');
    } finally {
      setAddingEmail(false);
    }
  };

  const removeEmail = async (emailData, e) => {
    e.stopPropagation();
    const email = typeof emailData === 'string' ? emailData : emailData.email;
    const entryId = typeof emailData === 'object' ? emailData.id : null;

    if (!window.confirm(`Remove all permissions for ${email}?`)) return;

    try {
      // Remove from allowed_email whitelist table
      if (entryId) {
        await ApiService.delete(`/access/whitelist/${entryId}`);
      } else {
        // Fallback: Use new whitelist-by-email endpoint if no ID provided
        console.warn(`[removeEmail] No entry ID for ${email}, using whitelist-by-email endpoint`);
        await ApiService.delete(`/access/whitelist-by-email?email=${encodeURIComponent(email)}`);
      }
      setAllEmails(prev => prev.filter(x => x.email !== email));
      showSnackbar('Email removed successfully', 'success');
    } catch (err) {
      console.error('Failed to remove email', err);
      const errorMsg = err.response?.data?.message || err.message || 'Failed to remove email';
      showSnackbar(errorMsg, 'error');
    }
  };

  /**
   * ✅ FIX: Populate missing emails in GMB accounts
   * This fixes the issue where emails show "0 GMB profiles" even though they have profiles
   */
  const handleFixEmails = async () => {
    if (!window.confirm('This will populate email fields for all GMB accounts. Continue?')) return;

    setLoading(true);
    try {
      const resp = await ApiService.post('/admin/gmb-account/fix-emails');
      const { fixed = 0, failed = 0, total = 0 } = resp?.data || resp || {};

      if (fixed > 0) {
        showSnackbar(`✅ Fixed ${fixed} of ${total} GMB accounts`, 'success');
        // Reload emails to reflect changes
        setTimeout(() => loadEmails(currentPage, debouncedSearch), 1000);
      } else if (total === 0) {
        showSnackbar('All GMB accounts already have emails', 'info');
      } else {
        showSnackbar(`⚠️ ${failed} accounts could not be fixed`, 'warning');
      }
    } catch (err) {
      console.error('Failed to fix emails', err);
      showSnackbar('Failed to fix emails: ' + (err.response?.data?.message || err.message), 'error');
    } finally {
      setLoading(false);
    }
  };

  /**
   * ✅ NEW: Clean up duplicate GMB records in database
   * This removes duplicate records for the same location_id + user_id combination
   */
  const handleCleanupDuplicates = async () => {
    if (!window.confirm('This will permanently remove duplicate GMB records from the database. Continue?')) return;

    setLoading(true);
    try {
      const resp = await ApiService.post('/admin/gmb-account/cleanup-duplicates');
      const { duplicateGroupsFound = 0, recordsDeleted = 0 } = resp?.data || resp || {};

      if (recordsDeleted > 0) {
        showSnackbar(`✅ Removed ${recordsDeleted} duplicate record(s) from ${duplicateGroupsFound} location(s)`, 'success');
        // Reload emails to reflect changes
        setTimeout(() => loadEmails(currentPage, debouncedSearch), 1000);
      } else {
        showSnackbar('✨ No duplicates found - database is clean!', 'success');
      }
    } catch (err) {
      console.error('Failed to cleanup duplicates', err);
      showSnackbar('Failed to cleanup duplicates: ' + (err.response?.data?.message || err.message), 'error');
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (email) => {
    if (!email) return '?';
    return email.substring(0, 2).toUpperCase();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getSubscriptionStatus = (sub) => {
    if (!sub) return 'none';
    if (sub.status === 'active' && new Date(sub.expires_at) > new Date()) return 'active';
    if (new Date(sub.expires_at) < new Date()) return 'expired';
    return sub.status || 'none';
  };

  const getPlanColor = (plan) => {
    const colors = {
      basic: '#10B981',
      advance: '#3B82F6',
      pro: '#8B5CF6'
    };
    return colors[plan] || '#64748B';
  };

  return (
    <PageContainer>
      {/* Header */}
      <HeaderSection>
        <PageTitle>
          <StorefrontIcon sx={{ fontSize: 32, color: '#3B82F6' }} />
          GMB Plans
        </PageTitle>
        <PageSubtitle>
          Manage and assign plans to GMB profiles for each email
        </PageSubtitle>
      </HeaderSection>

      {/* Stats */}
      <StatsRow>
        <StatCard>
          <StatIcon color="#3B82F6">
            <EmailIcon />
          </StatIcon>
          <Box>
            <StatValue>{stats.totalEmails}</StatValue>
            <StatLabel>Total Emails</StatLabel>
          </Box>
        </StatCard>
        <StatCard>
          <StatIcon color="#8B5CF6">
            <BusinessIcon />
          </StatIcon>
          <Box>
            <StatValue>{stats.totalGmbs}</StatValue>
            <StatLabel>GMB Profiles</StatLabel>
          </Box>
        </StatCard>
        <StatCard>
          <StatIcon color="#10B981">
            <VerifiedIcon />
          </StatIcon>
          <Box>
            <StatValue>{stats.withPlans}</StatValue>
            <StatLabel>With Plans</StatLabel>
          </Box>
        </StatCard>
        <StatCard>
          <StatIcon color="#F59E0B">
            <CheckCircleIcon />
          </StatIcon>
          <Box>
            <StatValue>{stats.activeSubscriptions}</StatValue>
            <StatLabel>Active Subscriptions</StatLabel>
          </Box>
        </StatCard>
      </StatsRow>

      {/* Toolbar */}
      <ToolbarSection>
        <SearchField
          placeholder="Search by email or business name..."
          size="small"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setCurrentPage(1);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#94A3B8' }} />
              </InputAdornment>
            ),
          }}
        />

        <Box sx={{ display: 'flex', gap: '12px' }}>
          <Tooltip title="Remove duplicate GMB records from database">
            <Button
              variant="outlined"
              size="small"
              onClick={handleCleanupDuplicates}
              disabled={loading}
              sx={{
                borderRadius: '10px',
                borderColor: '#F59E0B',
                color: '#F59E0B',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  borderColor: '#D97706',
                  backgroundColor: '#FEF3C7'
                }
              }}
            >
              Cleanup Duplicates
            </Button>
          </Tooltip>
          <Tooltip title="Fix missing emails in GMB accounts">
            <Button
              variant="outlined"
              size="small"
              onClick={handleFixEmails}
              disabled={loading}
              sx={{
                borderRadius: '10px',
                borderColor: '#E2E8F0',
                color: '#64748B',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  borderColor: '#CBD5E1',
                  backgroundColor: '#F8FAFC'
                }
              }}
            >
              Fix Emails
            </Button>
          </Tooltip>
          <Tooltip title="Refresh">
            <RefreshButton onClick={loadEmails} disabled={loading}>
              <RefreshIcon sx={{ color: loading ? '#CBD5E1' : '#64748B' }} />
            </RefreshButton>
          </Tooltip>
          <AddButton startIcon={<PersonAddIcon />} onClick={() => setAddEmailDialogOpen(true)}>
            Add Email
          </AddButton>

        </Box>
      </ToolbarSection>


      {/* Loading State */}
      {loading && (
        <Box sx={{ mb: 3 }}>
          <LinearProgress sx={{ borderRadius: '4px' }} />
        </Box>
      )}

      {/* Email List */}
      {!loading && paginatedEmails.length === 0 ? (
        <EmptyState>
          <EmailIcon sx={{ fontSize: 64, color: '#CBD5E1', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#64748B', mb: 1 }}>
            {searchQuery ? 'No results found' : 'No emails yet'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#94A3B8' }}>
            {searchQuery ? 'Try a different search term' : 'Click "Add Email" to get started'}
          </Typography>
        </EmptyState>
      ) : (
        <>
          {paginatedEmails.map(item => (
            <EmailCard key={item.email} isExpanded={!!expanded[item.email]}>
              <EmailCardHeader onClick={() => toggleExpand(item.email)}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1 }}>
                  <EmailAvatar>{getInitials(item.email)}</EmailAvatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: '15px', color: '#1E293B' }}>
                      {item.email}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                      <Typography sx={{ fontSize: '13px', color: '#64748B' }}>
                        {(item.gmbs || []).length} GMB profile(s)
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Tooltip title="Remove email">
                    <IconButton
                      size="small"
                      onClick={(e) => removeEmail(item, e)}
                      sx={{
                        color: '#94A3B8',
                        '&:hover': { color: '#EF4444', bgcolor: '#FEE2E2' }
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <IconButton size="small" sx={{ color: '#64748B' }}>
                    {expanded[item.email] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>
              </EmailCardHeader>

              <Collapse in={expanded[item.email]}>
                <Box sx={{ px: '20px', pb: '20px' }}>
                  <Divider sx={{ mb: 2 }} />

                  {loadingProfiles[item.email] ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {[1, 2].map(i => (
                        <Skeleton key={i} variant="rounded" height={100} sx={{ borderRadius: '10px' }} />
                      ))}
                    </Box>
                  ) : (profilesByEmail[item.email] || item.gmbs || []).length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 3, color: '#94A3B8' }}>
                      <BusinessIcon sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
                      <Typography variant="body2">No GMB profiles linked</Typography>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {(profilesByEmail[item.email] || item.gmbs || [])
                        .filter((g, idx, arr) => arr.findIndex(x => x.id === g.id) === idx)
                        .map((g, idx) => {
                          // gmbId is the database ID - unique per user per profile
                          // This is what we use for ALL operations (plan, AI limit, monthly plan)
                          const gmbId = String(g.id);
                          const currentPlan = planSelectionByGmb[gmbId] || g.plan;
                          const subscription = subscriptionsByGmb[gmbId];
                          const subStatus = getSubscriptionStatus(subscription);

                          return (
                            <GmbCard key={`${gmbId}-${idx}`}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                                {/* GMB Info */}
                                <Box sx={{ flex: 1, minWidth: '200px' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <Typography sx={{ fontWeight: 600, fontSize: '14px', color: '#1E293B' }}>
                                      {g.gmbName || 'Unnamed Business'}
                                    </Typography>
                                    {g.isVerified && (
                                      <Tooltip title="Verified">
                                        <VerifiedIcon sx={{ fontSize: 16, color: '#3B82F6' }} />
                                      </Tooltip>
                                    )}
                                    {/* Show current assigned plan */}
                                    {currentPlan && (
                                      <SharedPlanChip plan={currentPlan} />
                                    )}
                                  </Box>
                                  {g.address && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#64748B' }}>
                                      <LocationOnIcon sx={{ fontSize: 14 }} />
                                      <Typography sx={{ fontSize: '12px' }}>{g.address}</Typography>
                                    </Box>
                                  )}
                                  <Typography sx={{ fontSize: '11px', color: '#94A3B8', mt: 0.5 }}>
                                    ID: {gmbId}
                                  </Typography>
                                </Box>

                                {/* Subscription Status */}
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                                  <Box sx={{ display: 'inline-flex', cursor: subStatus === 'active' ? 'default' : 'pointer' }}
                                    onClick={async () => {
                                      if (subStatus === 'active') return;
                                      try {
                                        showSnackbar('Activating subscription...', 'info');
                                        // Use gmb_account_id for subscription activation
                                        await ApiService.post('/admin/gmb-account/activate-subscription', { gmb_account_id: gmbId });
                                        await fetchSubscriptionForGmb(gmbId);
                                        showSnackbar('Subscription activated', 'success');
                                      } catch (err) {
                                        console.error('Activation failed', err);
                                        showSnackbar('Failed to activate subscription', 'error');
                                      }
                                    }}
                                  >
                                    <SubscriptionBadge status={subStatus}>
                                      {subStatus === 'active' && <CheckCircleIcon sx={{ fontSize: 12 }} />}
                                      {subStatus === 'expired' && <CancelIcon sx={{ fontSize: 12 }} />}
                                      {subStatus === 'pending' && <AccessTimeIcon sx={{ fontSize: 12 }} />}
                                      {subStatus === 'none' && <CancelIcon sx={{ fontSize: 12 }} />}
                                      {subStatus === 'active' ? 'Active' : subStatus === 'expired' ? 'Expired' : subStatus === 'pending' ? 'Pending' : 'No Subscription'}
                                    </SubscriptionBadge>
                                  </Box>
                                  {subscription?.expires_at && (
                                    <Typography sx={{ fontSize: '11px', color: '#94A3B8' }}>
                                      {subStatus === 'active' ? 'Expires' : 'Expired'}: {formatDate(subscription.expires_at)}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>

                              {/* Plan Selection */}
                              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography sx={{ fontSize: '12px', color: '#64748B', mr: 1 }}>Plan:</Typography>
                                  <PlanSelectorChip
                                    label="Basic"
                                    selected={currentPlan === 'basic'}
                                    plancolor="#10B981"
                                    onClick={() => setPlanSelectionByGmb(prev => ({ ...prev, [gmbId]: 'basic' }))}
                                  />
                                  <PlanSelectorChip
                                    label="Advance"
                                    selected={currentPlan === 'advance'}
                                    plancolor="#3B82F6"
                                    onClick={() => setPlanSelectionByGmb(prev => ({ ...prev, [gmbId]: 'advance' }))}
                                  />
                                  <PlanSelectorChip
                                    label="Pro"
                                    selected={currentPlan === 'pro'}
                                    plancolor="#8B5CF6"
                                    onClick={() => setPlanSelectionByGmb(prev => ({ ...prev, [gmbId]: 'pro' }))}
                                  />
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <SavePlanButton
                                    startIcon={savingPlan[gmbId] ? <CircularProgress size={14} color="inherit" /> : <SaveIcon sx={{ fontSize: 16 }} />}
                                    onClick={() => assignPlan(gmbId, currentPlan)}
                                    disabled={!currentPlan || savingPlan[gmbId]}
                                  >
                                    {savingPlan[gmbId] ? 'Saving...' : 'Save Plan'}
                                  </SavePlanButton>
                                  {currentPlan && (
                                    <Tooltip title="Remove plan from this profile">
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={savingPlan[gmbId] ? <CircularProgress size={14} /> : <DeleteOutlineIcon sx={{ fontSize: 16 }} />}
                                        onClick={() => removePlan(gmbId)}
                                        disabled={savingPlan[gmbId]}
                                        sx={{
                                          borderRadius: '8px',
                                          borderColor: '#EF4444',
                                          color: '#EF4444',
                                          fontWeight: 600,
                                          fontSize: '12px',
                                          textTransform: 'none',
                                          padding: '6px 16px',
                                          '&:hover': {
                                            borderColor: '#DC2626',
                                            backgroundColor: '#FEE2E2',
                                          },
                                        }}
                                      >
                                        Remove
                                      </Button>
                                    </Tooltip>
                                  )}
                                </Box>
                              </Box>

                              {/* AI Post Limit Selection */}
                              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  <Typography sx={{ fontSize: '12px', color: '#64748B', fontWeight: 500 }}>
                                    AI Posts: {aiPostLimitByGmb[gmbId] !== null && aiPostLimitByGmb[gmbId] !== undefined ? <span style={{ color: '#8B5CF6', fontWeight: 700 }}>({aiPostLimitByGmb[gmbId]})</span> : <span style={{ color: '#94A3B8' }}>(not set)</span>}
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                    {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                                      <Chip
                                        key={num}
                                        label={num === 0 ? '0' : num}
                                        size="small"
                                        onClick={() => setAiPostLimitByGmb(prev => ({ ...prev, [gmbId]: num }))}
                                        sx={{
                                          minWidth: '32px',
                                          height: '28px',
                                          fontWeight: 600,
                                          fontSize: '12px',
                                          cursor: 'pointer',
                                          transition: 'all 0.2s ease',
                                          backgroundColor: aiPostLimitByGmb[gmbId] === num ? (num === 0 ? '#EF4444' : '#8B5CF6') : '#F1F5F9',
                                          color: aiPostLimitByGmb[gmbId] === num ? '#fff' : '#64748B',
                                          border: aiPostLimitByGmb[gmbId] === num ? 'none' : '1px solid #E2E8F0',
                                          '&:hover': {
                                            backgroundColor: aiPostLimitByGmb[gmbId] === num ? (num === 0 ? '#DC2626' : '#7C3AED') : '#E2E8F0',
                                            transform: 'scale(1.05)',
                                          },
                                        }}
                                      />
                                    ))}
                                  </Box>
                                </Box>
                                <SavePlanButton
                                  startIcon={savingAiPostLimit[gmbId] ? <CircularProgress size={14} color="inherit" /> : <SaveIcon sx={{ fontSize: 16 }} />}
                                  onClick={() => saveAiPostLimit(gmbId, aiPostLimitByGmb[gmbId])}
                                  disabled={aiPostLimitByGmb[gmbId] === null || aiPostLimitByGmb[gmbId] === undefined || savingAiPostLimit[gmbId]}
                                  sx={{ backgroundColor: '#8B5CF6', '&:hover': { backgroundColor: '#7C3AED' } }}
                                >
                                  {savingAiPostLimit[gmbId] ? 'Saving...' : 'Save AI Limit'}
                                </SavePlanButton>
                              </Box>

                              {/* Monthly Plan Toggle */}
                              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  <Typography sx={{ fontSize: '12px', color: '#64748B', fontWeight: 500 }}>Monthly Plan Access:</Typography>
                                  <FormControlLabel
                                    control={
                                      <Switch
                                        checked={!!monthlyPlanByGmb[gmbId]}
                                        onChange={(e) => toggleMonthlyPlan(gmbId, e.target.checked)}
                                        disabled={savingMonthlyPlan[gmbId]}
                                        sx={{
                                          '& .MuiSwitch-switchBase.Mui-checked': {
                                            color: '#667eea',
                                          },
                                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                            backgroundColor: '#667eea',
                                          },
                                        }}
                                      />
                                    }
                                    label={
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography sx={{ fontSize: '12px', color: monthlyPlanByGmb[gmbId] ? '#667eea' : '#94A3B8', fontWeight: 600 }}>
                                          {monthlyPlanByGmb[gmbId] ? 'ENABLED' : 'DISABLED'}
                                        </Typography>
                                        {savingMonthlyPlan[gmbId] && <CircularProgress size={12} sx={{ color: '#667eea' }} />}
                                      </Box>
                                    }
                                    labelPlacement="end"
                                    sx={{ ml: 0 }}
                                  />
                                </Box>
                                <Typography sx={{ fontSize: '11px', color: '#94A3B8' }}>
                                  {monthlyPlanByGmb[gmbId] ? '✨ User can access Monthly Plan page' : '🔒 Monthly Plan page is locked'}
                                </Typography>
                              </Box>
                            </GmbCard>
                          );
                        })}
                    </Box>
                  )}
                </Box>
              </Collapse>
            </EmailCard>
          ))}

          {/* Pagination - uses server-side pagination */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: 3 }}>
            {/* Show pagination info */}
            <Typography sx={{ fontSize: '13px', color: '#64748B' }}>
              Showing {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, serverPagination.totalItems)} of {serverPagination.totalItems} emails
            </Typography>

            {/* Pagination controls */}
            {serverPagination.totalPages > 1 && (
              <Pagination
                count={serverPagination.totalPages}
                page={currentPage}
                onChange={(e, v) => setCurrentPage(v)}
                color="primary"
                shape="rounded"
                showFirstButton
                showLastButton
                sx={{
                  '& .MuiPaginationItem-root': {
                    borderRadius: '8px',
                    fontWeight: 500,
                  },
                }}
              />
            )}
          </Box>
        </>
      )}

      {/* Add Email Dialog */}
      <Dialog
        open={addEmailDialogOpen}
        onClose={() => setAddEmailDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            minWidth: '400px',
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>
          Add New Email
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#64748B', mb: 2 }}>
            Add an email address to grant access to the platform. You can then assign GMB profiles and plans.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Email Address"
            placeholder="user@example.com"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddEmail()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon sx={{ color: '#94A3B8' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setAddEmailDialogOpen(false)}
            sx={{ borderRadius: '8px', textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddEmail}
            disabled={addingEmail}
            startIcon={addingEmail ? <CircularProgress size={16} color="inherit" /> : <PersonAddIcon />}
            sx={{
              borderRadius: '8px',
              textTransform: 'none',
              bgcolor: '#3B82F6',
              '&:hover': { bgcolor: '#2563EB' },
            }}
          >
            {addingEmail ? 'Adding...' : 'Add Email'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Trial emails UI removed */}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ borderRadius: '10px', fontWeight: 500 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
}
